import React, { ReactElement, useEffect, useState } from 'react';
import { BarLoader } from 'react-spinners';
import {
  useGetEventInfoMutation,
  useGetGiftTypesMutation,
} from '../../../../services/api/apiProvider';
import {
  ResponseEvent,
  ResponsePriorityEvents,
} from '../../../../services/api/models/response/responseEvent';
import {
  ResponseGift,
  ResponseGiftCategory,
} from '../../../../services/api/models/response/responseGift';
import './EventGiftTab.scss';
import { GiftItem } from './GiftItem';

interface EventGiftTabProps {
  giftCategory: ResponseGiftCategory;
  events: ResponsePriorityEvents[];
}

export const EventGiftTab = (props: EventGiftTabProps): ReactElement => {
  const [loading, setLoading] = useState(true);
  const [eventGiftData, setEventGiftData] = useState<EventGiftProps[]>([]);
  const [otherGiftData, setOtherGiftData] = useState<ResponseGift[]>([]);
  const [getGiftType] = useGetGiftTypesMutation();
  const [getEventInfo] = useGetEventInfoMutation();

  const fetchEventGiftData = async (): Promise<EventGiftProps[]> => {
    const eventGifts: EventGiftProps[] = [];

    for (const pe of props.events) {
      if (!pe.event) {
        return eventGifts;
      }

      const event = await getEventInfo(pe.event.id).unwrap();
      const gifts = await getGiftType({
        eventId: pe.event?.id,
        giftCategoryId: props.giftCategory.id,
      }).unwrap();

      eventGifts.push({
        event,
        gifts,
      });
    }

    return eventGifts;
  };

  const fetchTotalGiftData = async (): Promise<ResponseGift[]> => {
    return await getGiftType({
      giftCategoryId: props.giftCategory.id,
    }).unwrap();
  };

  const getOtherGifts = (
    totalGift: ResponseGift[],
    eventGifts: EventGiftProps[],
  ) => {
    const others: ResponseGift[] = [];
    let totalEventGifts: ResponseGift[] = [];

    eventGifts.forEach((eg) => {
      totalEventGifts = totalEventGifts.concat(eg.gifts);
    });

    totalGift.forEach((gift) => {
      const eventGift = totalEventGifts.find((eg) => eg.id === gift.id);
      if (!eventGift) {
        others.push(gift);
      }
    });

    return others;
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchEventGiftData(), fetchTotalGiftData()]).then((values) => {
      const [eventGifts, totalGift] = values;
      setEventGiftData(eventGifts);
      const others = getOtherGifts(totalGift, eventGifts);
      setOtherGiftData(others);
      setLoading(false);
    });
  }, []);

  return (
    <div className="event-gift-tab">
      {loading ? (
        <div className="event-gift-tab__loader">
          <BarLoader color={'#0275d8'} width="100%"></BarLoader>
        </div>
      ) : (
        <>
          {eventGiftData.length <= 0 ? (
            <p className="event-gift__title">
              定番ギフトで応援しよう！<br></br>
              ※このイベントには指定ギフトがありません。
            </p>
          ) : (
            <>
              {eventGiftData.map((eg, i) => (
                <EventGift key={i} event={eg.event} gifts={eg.gifts} />
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
};

interface EventGiftProps {
  event: ResponseEvent;
  gifts: ResponseGift[];
}

export const EventGift = (props: EventGiftProps): ReactElement => {
  const { event, gifts } = props;
  const [isOutOfDate, setIsOutOfDate] = useState(false);
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const endDate = new Date(event.endDateTime);
      if (now > endDate) {
        clearInterval(timer);
        setIsOutOfDate(true);
        return;
      }
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);
  return (
    <div className="event-gift">
      {isOutOfDate && (
        <div className="event-gift-disable">
          イベントは終了しました しばらくお待ちください・・・
        </div>
      )}
      <img className="event-gift__banner" src={event.giftBanner?.uri} />
      <div className="event-gift__list-gift">
        <div className="list-gift">
          {gifts.map((gift) => (
            <GiftItem key={gift.id} gift={gift} />
          ))}
        </div>
      </div>
    </div>
  );
};
